import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './context/AuthContext';

// Keep in sync with core/auth/roles.py VALID_ROLES. social_media grants no
// access to this engine at all — it reads the marketplace admin's Growth
// page and nothing else.
const ROLES = ['annotator', 'user', 'superuser', 'social_media'];

// User management. Credentials are managed by Supabase — this page manages the
// local profile concerns: role and approval (`is_active`). Approving a pending
// signup is just toggling it to "active" and saving. Password resets are done
// by the user through Supabase ("Forgot password?" on the login screen).
const UsersAdminPage = () => {
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'user' });
  const [busy, setBusy] = useState(false);
  const [pendingRoles, setPendingRoles] = useState({});
  const [pendingActive, setPendingActive] = useState({});
  const [savingAll, setSavingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get('/api/users');
      setUsers(r.data.users || []);
      setPendingRoles({});
      setPendingActive({});
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const payload = { ...form, email: form.email.trim() };
      const r = await axios.post('/api/users', payload);
      const created = r.data?.user;
      setForm({ username: '', email: '', password: '', role: 'user' });
      if (created) {
        setUsers((prev) => [...prev, created]);
      } else {
        await load();
      }
    } catch (e) {
      const data = e?.response?.data;
      setError(data?.message || data?.error || 'Failed to create user');
    } finally {
      setBusy(false);
    }
  };

  const handleRoleSelect = (id, role) => {
    setPendingRoles((prev) => {
      const next = { ...prev };
      const u = users.find((x) => x.id === id);
      if (u && u.role === role) {
        delete next[id];
      } else {
        next[id] = role;
      }
      return next;
    });
  };

  const handleActiveToggle = (u) => {
    setPendingActive((prev) => {
      const next = { ...prev };
      const currentEffective = next[u.id] !== undefined ? next[u.id] : u.is_active;
      const newVal = !currentEffective;
      if (newVal === u.is_active) {
        delete next[u.id];
      } else {
        next[u.id] = newVal;
      }
      return next;
    });
  };

  const dirtyIds = useMemo(() => {
    const ids = new Set();
    Object.keys(pendingRoles).forEach((id) => ids.add(Number(id)));
    Object.keys(pendingActive).forEach((id) => ids.add(Number(id)));
    return ids;
  }, [pendingRoles, pendingActive]);

  const handleSaveAll = async () => {
    if (dirtyIds.size === 0) return;
    setSavingAll(true);
    setError('');
    try {
      const updates = Array.from(dirtyIds).map((id) => {
        const payload = {};
        if (pendingRoles[id] !== undefined) payload.role = pendingRoles[id];
        if (pendingActive[id] !== undefined) payload.is_active = pendingActive[id];
        return axios.patch(`/api/users/${id}`, payload);
      });
      const responses = await Promise.all(updates);
      const updated = responses
        .map((res) => res?.data?.user)
        .filter(Boolean);
      if (updated.length) {
        setUsers((prev) => prev.map((u) => updated.find((x) => x.id === u.id) || u));
      }
      setPendingRoles({});
      setPendingActive({});
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save changes');
    } finally {
      setSavingAll(false);
    }
  };

  const handleDiscardAll = () => {
    setPendingRoles({});
    setPendingActive({});
  };

  const handleDelete = async (id) => {
    if (id === currentUserId) {
      setError('You cannot delete your own account.');
      return;
    }
    if (!window.confirm('Delete this user? This also removes their Supabase account.')) return;
    try {
      await axios.delete(`/api/users/${id}`);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setPendingRoles((prev) => { const n = { ...prev }; delete n[id]; return n; });
      setPendingActive((prev) => { const n = { ...prev }; delete n[id]; return n; });
    } catch (e) {
      const code = e?.response?.data?.error;
      const msg = code === 'cannot_delete_self'
        ? 'You cannot delete your own account.'
        : (code || 'Failed to delete user');
      setError(msg);
    }
  };

  const dirtyCount = dirtyIds.size;
  const pendingApprovalCount = useMemo(
    () => users.filter((u) => !u.is_active).length,
    [users],
  );
  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => (a.id || 0) - (b.id || 0)),
    [users],
  );

  return (
    <div className="max-w-4xl mx-auto bg-surface rounded-lg shadow border border-hairline p-4 sm:p-6">
      <h1 className="text-xl font-bold text-ink mb-4">User management</h1>

      <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-6">
        <input
          className="bg-field border border-hairline text-ink placeholder:text-muted rounded px-2 py-1"
          placeholder="username"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
          required
        />
        <input
          className="bg-field border border-hairline text-ink placeholder:text-muted rounded px-2 py-1"
          type="email"
          placeholder="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
        />
        <input
          className="bg-field border border-hairline text-ink placeholder:text-muted rounded px-2 py-1"
          type="password"
          placeholder="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required
        />
        <select
          className="bg-field border border-hairline text-ink placeholder:text-muted rounded px-2 py-1"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button
          type="submit"
          disabled={busy}
          className="bg-brand text-white rounded px-3 py-1 hover:bg-brand-hover disabled:opacity-50"
        >
          Create user
        </button>
      </form>

      {error && <div className="mb-3 text-sm text-danger">{error}</div>}

      {pendingApprovalCount > 0 && (
        <div className="mb-3 text-sm rounded border border-warn/30 bg-warn/10 text-warn px-3 py-2">
          {pendingApprovalCount} account{pendingApprovalCount === 1 ? '' : 's'} awaiting approval. Toggle a row to <b>active</b> and save to approve.
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-muted">
              {dirtyCount > 0
                ? `${dirtyCount} unsaved change${dirtyCount === 1 ? '' : 's'}`
                : 'No unsaved changes'}
            </div>
            <div className="space-x-2">
              <button
                onClick={handleDiscardAll}
                disabled={dirtyCount === 0 || savingAll}
                className="border border-hairline rounded px-3 py-1 text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Discard
              </button>
              <button
                onClick={handleSaveAll}
                disabled={dirtyCount === 0 || savingAll}
                className="bg-brand text-white rounded px-3 py-1 hover:bg-brand-hover disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {savingAll ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-muted border-b border-hairline">
              <tr>
                <th className="py-2">ID</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Active</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => {
                const effectiveRole = pendingRoles[u.id] ?? u.role;
                const effectiveActive = pendingActive[u.id] !== undefined ? pendingActive[u.id] : u.is_active;
                const rowDirty = dirtyIds.has(u.id);
                const rowBg = rowDirty
                  ? 'bg-warn/10'
                  : (!u.is_active ? 'bg-warn/10' : '');
                return (
                  <tr key={u.id} className={`border-b border-hairline last:border-b-0 ${rowBg}`}>
                    <td className="py-2">{u.id}</td>
                    <td>{u.username}</td>
                    <td className="text-muted break-all">{u.email || <span className="text-muted">—</span>}</td>
                    <td>
                      <select
                        value={effectiveRole}
                        onChange={(e) => handleRoleSelect(u.id, e.target.value)}
                        className="bg-surface border border-hairline text-ink rounded px-1 py-0.5"
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td>
                      <button
                        onClick={() => handleActiveToggle(u)}
                        className={`px-2 py-0.5 rounded ${effectiveActive ? 'bg-positive/10 text-positive' : 'bg-surface-2 text-muted'}`}
                      >
                        {effectiveActive ? 'active' : 'disabled'}
                      </button>
                    </td>
                    <td className="text-muted">{u.created_at?.replace('T', ' ').slice(0, 19)}</td>
                    <td className="space-x-2 text-right">
                      {u.id !== currentUserId ? (
                        <button onClick={() => handleDelete(u.id)} className="text-danger hover:underline">delete</button>
                      ) : (
                        <span aria-hidden="true" className="invisible select-none">delete</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default UsersAdminPage;
